/**
 * EngagementsBell.jsx — Phase 7 Sprint 7.4 follow-up.
 *
 * Header bell with the total unread-message count across every
 * enterprise engagement channel the caller can see. Reuses the
 * Sprint 7.4 inbox endpoints:
 *
 *   admin:  GET /api/admin/enterprise-intake/_engagements/inbox?only_unread=true
 *   client: GET /api/account/enterprise-intake/_engagements/unread
 *
 * Behaviour:
 *   - Polls every 45 s while mounted.
 *   - Renders a Bell icon; red pill badge appears only when
 *     total_unread > 0.
 *   - Click navigates:
 *       admin  → /admin/enterprise-engagements/inbox
 *       client → /account/enterprise/{first_unread_intake_id}
 *               (or /account if the client has no engagements yet).
 *   - Auth failures (401/403) silently zero the count — the bell
 *     just sits dim. We never throw a toast for a background poll.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, X } from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 45_000;
const NOTIF_DISMISS_KEY = "qt-engagements-bell-notif-dismissed";


/* ───────────── tab-title flashing + audio ping helpers ─────────────
   Module-level singletons so the count rising on EITHER bell (admin
   or client, if a user has both open in the same tab) only schedules
   one title-flash + one ping. */

let _origTitle = null;
let _flashTimer = null;
let _flashOn = false;

function _captureOrigTitle() {
  if (_origTitle === null && typeof document !== "undefined") {
    _origTitle = document.title.replace(/^\(\d+\)\s+/, "");
  }
  return _origTitle;
}

function _setTitleBadge(count) {
  if (typeof document === "undefined") return;
  const base = _captureOrigTitle() ?? document.title;
  document.title = count > 0 ? `(${count > 99 ? "99+" : count}) ${base}` : base;
}

function _restoreTitle() {
  if (typeof document === "undefined") return;
  if (_flashTimer) { clearInterval(_flashTimer); _flashTimer = null; }
  _flashOn = false;
  if (_origTitle !== null) document.title = _origTitle;
}

function _startTitleFlash(count) {
  _captureOrigTitle();
  if (_flashTimer) clearInterval(_flashTimer);
  _flashOn = true;
  _setTitleBadge(count);
  _flashTimer = setInterval(() => {
    _flashOn = !_flashOn;
    if (_flashOn) _setTitleBadge(count);
    else if (_origTitle !== null) document.title = _origTitle;
  }, 1100);
}

function _playPing() {
  if (typeof window === "undefined") return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const now = ctx.currentTime;

    // Two-tone gentle chime: a soft G5 → C6 with a slow decay,
    // ~280 ms total. Quiet (peak 0.07 gain) so it never startles
    // someone wearing headphones. Falls back silently if the
    // browser blocks autoplay (e.g. before the user has interacted
    // with the page).
    const tones = [
      { freq: 784.0, start: 0.00, dur: 0.18 },   // G5
      { freq: 1046.5, start: 0.09, dur: 0.20 },  // C6
    ];

    for (const t of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = t.freq;
      gain.gain.setValueAtTime(0, now + t.start);
      gain.gain.linearRampToValueAtTime(0.07, now + t.start + 0.015);
      gain.gain.exponentialRampToValueAtTime(
        0.0001, now + t.start + t.dur,
      );
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur + 0.02);
    }
    // Close the context after the last tone finishes.
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 600);
  } catch { /* autoplay blocked / WebAudio unavailable — silent fail */ }
}


/* ───────────── Native desktop notification helpers ─────────────────
   Uses the in-tab `Notification` API. Fires a native OS toast when
   unread rises while the tab is hidden. Works while the tab is open
   (foreground or background); does NOT survive tab close (a full
   Web Push + service worker stack would — separate iteration). */

function _notificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

function _notificationPermission() {
  if (!_notificationsSupported()) return "unsupported";
  return Notification.permission;
}

async function _requestNotificationPermission() {
  if (!_notificationsSupported()) return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

function _fireDesktopNotification({ count, body, onClickNavigate }) {
  if (!_notificationsSupported()) return;
  if (Notification.permission !== "granted") return;
  try {
    const title = count === 1
      ? "1 unread message · QorTrace"
      : `${count > 99 ? "99+" : count} unread messages · QorTrace`;
    const n = new Notification(title, {
      body: (body || "").slice(0, 200) || "Open QorTrace to reply.",
      // `tag` dedupes — a second notification with the same tag
      // replaces the first instead of stacking up.
      tag: "qt-engagements",
      renotify: true,
      icon: "/favicon-32.png",
      badge: "/favicon-32.png",
      silent: false,
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* noop */ }
      try { n.close(); } catch { /* noop */ }
      try { onClickNavigate?.(); } catch { /* noop */ }
    };
  } catch { /* fail closed — flash + ping still cover it */ }
}


export default function EngagementsBell({ kind = "client" }) {
  const [unread, setUnread] = useState(0);
  const [firstId, setFirstId] = useState(null);
  const [notifPerm, setNotifPerm] = useState(() => _notificationPermission());
  const [notifDismissed, setNotifDismissed] = useState(() => {
    try { return localStorage.getItem(NOTIF_DISMISS_KEY) === "1"; }
    catch { return false; }
  });
  const timerRef = useRef(null);
  const prevUnreadRef = useRef(null);   // null on first load → no ping
  const lastBodyRef = useRef("");       // most-recent message preview
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      let nextUnread = 0;
      let nextFirstId = null;
      let nextBody = "";
      if (kind === "admin") {
        const r = await axios.get(
          `${API}/api/admin/enterprise-intake/_engagements/inbox`,
          { withCredentials: true,
            params: { only_unread: "true", limit: 5 } },
        );
        nextUnread = r?.data?.total_unread || 0;
        const top = r?.data?.items?.[0];
        nextFirstId = top?.intake_id || null;
        const lastMsg = top?.last_message;
        if (lastMsg?.body) {
          const author = lastMsg.author_name
            || lastMsg.author_email?.split("@")[0]
            || "A client";
          nextBody = `${author}: ${lastMsg.body}`;
        }
      } else {
        const r = await axios.get(
          `${API}/api/account/enterprise-intake/_engagements/unread`,
          { withCredentials: true },
        );
        nextUnread = r?.data?.total_unread || 0;
        const firstUnread = (r?.data?.items || [])
          .find((it) => (it.unread || 0) > 0);
        nextFirstId = firstUnread?.intake_id || null;
        if (firstUnread) {
          nextBody = `${firstUnread.assigned_ae_name || "Your AE"} · ${firstUnread.company_name || ""}`;
        }
      }
      lastBodyRef.current = nextBody;

      // ── New-message side effects ────────────────────────────────
      // Only fire when the count RISES (a new unread arrived) AND
      // either the tab is in the background OR there was no prior
      // count yet (the initial poll skips both flash and ping so
      // simply refreshing the page is silent).
      const prev = prevUnreadRef.current;
      if (prev !== null && nextUnread > prev) {
        if (typeof document !== "undefined" && document.hidden) {
          _startTitleFlash(nextUnread);
          _playPing();
          // Native OS toast — only when permission has been granted.
          // Routes click back to the appropriate engagement.
          _fireDesktopNotification({
            count: nextUnread,
            body:  nextBody,
            onClickNavigate: () => {
              if (kind === "admin") {
                navigate("/admin/enterprise-engagements/inbox");
              } else if (nextFirstId) {
                navigate(`/account/enterprise/${nextFirstId}`);
              }
            },
          });
        } else if (nextUnread > 0) {
          // Tab is focused — refresh the badge text without flashing
          // so the count in the title stays accurate.
          _setTitleBadge(nextUnread);
        }
      } else if (prev !== null && nextUnread === 0) {
        _restoreTitle();
      }
      prevUnreadRef.current = nextUnread;

      setUnread(nextUnread);
      setFirstId(nextFirstId);
    } catch {
      // Background poll — don't surface auth failures or transient
      // network errors. Just leave the count at its previous value.
      setUnread(0);
      setFirstId(null);
      prevUnreadRef.current = 0;
      _restoreTitle();
    }
  }, [kind]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);

    // Stop flashing the title the moment the user returns to the
    // tab. The count is still surfaced by the bell badge itself.
    const onVis = () => {
      if (!document.hidden) _restoreTitle();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
      _restoreTitle();
    };
  }, [load]);

  const onClick = () => {
    if (kind === "admin") {
      navigate("/admin/enterprise-engagements/inbox");
    } else if (firstId) {
      navigate(`/account/enterprise/${firstId}`);
    } else {
      navigate("/account");
    }
  };

  const onEnableNotifications = async (e) => {
    e.stopPropagation();
    const result = await _requestNotificationPermission();
    setNotifPerm(result);
    if (result !== "granted") {
      // Treat a denial as a permanent dismissal so we don't ask
      // again. The user can re-grant from the browser settings.
      try { localStorage.setItem(NOTIF_DISMISS_KEY, "1"); }
      catch { /* private-mode storage blocked */ }
      setNotifDismissed(true);
    }
  };

  const onDismissNotifications = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(NOTIF_DISMISS_KEY, "1"); }
    catch { /* private-mode storage blocked */ }
    setNotifDismissed(true);
  };

  // Show the opt-in chip only when (a) the browser supports the API,
  // (b) the user has not yet decided, (c) the user has not dismissed
  // it before, AND (d) there's at least one unread engagement (so
  // we never ask cold — only after the bell has earned attention).
  const showOptIn = (
    notifPerm === "default"
    && !notifDismissed
    && unread > 0
  );

  const hasUnread = unread > 0;
  const label = hasUnread
    ? `${unread} unread engagement message${unread === 1 ? "" : "s"}`
    : "No unread engagement messages";

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={`engagements-bell-${kind}`}
      data-unread={unread}
      data-notif-perm={notifPerm}
      style={{
        position: "relative",
        width: 32, height: 32,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "transparent",
        border: "1px solid var(--qt-border)",
        borderRadius: 6,
        cursor: "pointer",
        color: hasUnread ? "var(--qt-cyan)" : "var(--qt-text-2)",
        transition: "color 120ms, border-color 120ms, background 120ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(0,229,255,0.06)";
        e.currentTarget.style.borderColor = hasUnread
          ? "var(--qt-cyan)" : "var(--qt-text-2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = "var(--qt-border)";
      }}>
      {notifPerm === "granted"
        ? <BellRing size={14} />
        : <Bell size={14} />}
      {hasUnread && (
        <span
          data-testid={`engagements-bell-${kind}-badge`}
          style={{
            position: "absolute", top: -5, right: -5,
            minWidth: 16, height: 16, padding: "0 4px",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            borderRadius: 9,
            background: "#FF6E6E",
            color: "#0F172A",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, fontWeight: 800,
            lineHeight: 1, letterSpacing: 0,
            boxShadow: "0 0 0 2px rgba(4,9,20,0.95)",
          }}>
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
    {showOptIn && (
      <NotifOptInCard
          onEnable={onEnableNotifications}
          onDismiss={onDismissNotifications} />
    )}
    </span>
  );
}


function NotifOptInCard({ onEnable, onDismiss }) {
  // Positioned absolutely below the bell. Width is wide enough to
  // not crowd the wording but narrow enough to slot under both the
  // admin topbar and account sidebar bells.
  return (
    <div
      data-testid="engagements-bell-notif-optin"
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        width: 264,
        padding: "10px 12px",
        borderRadius: 6,
        background: "rgba(15,23,42,0.97)",
        border: "1px solid var(--qt-cyan)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
        zIndex: 100,
      }}>
      <div className="flex items-start gap-2">
        <BellRing size={13}
                  style={{ color: "var(--qt-cyan)",
                           marginTop: 1, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="qt-mono text-[10px] mb-1"
               style={{ color: "var(--qt-cyan)",
                        letterSpacing: "0.10em" }}>
            DESKTOP ALERTS
          </div>
          <div className="text-xs mb-2"
               style={{ color: "var(--qt-text-1)", lineHeight: 1.45 }}>
            Get a native notification when an AE sends a new message
            — even when this tab is in the background.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEnable}
              data-testid="engagements-bell-notif-enable"
              className="qt-mono text-[10px]"
              style={{
                padding: "4px 9px", borderRadius: 3,
                background: "var(--qt-cyan)", color: "#0F172A",
                border: "1px solid var(--qt-cyan)",
                fontWeight: 800, cursor: "pointer",
                letterSpacing: "0.06em",
              }}>
              ENABLE
            </button>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="engagements-bell-notif-dismiss"
              className="qt-mono text-[10px]"
              style={{
                padding: "4px 9px", borderRadius: 3,
                background: "transparent", color: "var(--qt-text-2)",
                border: "1px solid var(--qt-border)",
                cursor: "pointer", letterSpacing: "0.06em",
              }}>
              NOT NOW
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          style={{ background: "transparent", border: "none",
                   color: "var(--qt-text-3)", cursor: "pointer",
                   padding: 0, marginLeft: 2 }}>
          <X size={11} />
        </button>
      </div>
    </div>
  );
}
